import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styles from './TermsAndConditionsModal.module.scss';
import ModalOverlay from '../ModalOverlay/ModalOverlay.tsx';
import { FileText, X } from 'lucide-react';
import PDF from '../../../components/Footer/PDF/Terminos-y-condiciones-edify-2021.pdf';

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onCancel: () => void;
  projectType: 'proyecto' | 'anteproyecto';
}

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 20 },
  visible: {
    opacity: 1,
    scale: 1.0,
    y: 0,
    transition: { 
      delay: 0.1,
      duration: 0.3,
      ease: "easeOut"
    },
  },
};

const TermsAndConditionsModal: React.FC<TermsAndConditionsModalProps> = ({
  isOpen,
  onAccept,
  onCancel,
  projectType
}) => {
  const [hasAccepted, setHasAccepted] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom = Math.abs(
      element.scrollHeight - element.scrollTop - element.clientHeight
    ) < 10;
    
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAcceptClick = () => {
    if (hasAccepted) {
      onAccept();
    }
  };

  const handleClose = () => {
    setHasAccepted(false);
    setHasScrolledToBottom(false);
    onCancel();
  };

  return (
    <ModalOverlay 
      isOpen={isOpen} 
      preventClose={true}
      onClose={handleClose}
    >
      <motion.div
        className={styles.modal}
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
      >
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <FileText className={styles.headerIcon} />
            <div>
              <h2 className={styles.title}>Términos y Condiciones</h2>
              <p className={styles.subtitle}>
                Para publicar tu {projectType === 'proyecto' ? 'proyecto' : 'anteproyecto'} debes aceptar los términos y condiciones
              </p>
            </div>
          </div>
          <button 
            className={styles.closeButton} 
            onClick={handleClose}
            type="button"
          >
            <X size={20} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.pdfContainer} onScroll={handleScroll}>
            <iframe
              src={PDF}
              title="Términos y Condiciones"
              className={styles.pdfViewer}
            />
          </div>

          {!hasScrolledToBottom && (
            <div className={styles.scrollHint}>
              ↓ Desplázate para leer todos los términos y condiciones
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.checkboxContainer}>
            <input
              type="checkbox"
              id="acceptTerms"
              checked={hasAccepted}
              onChange={(e) => setHasAccepted(e.target.checked)}
              className={styles.checkbox}
            />
            <label htmlFor="acceptTerms" className={styles.checkboxLabel}>
              He leído y acepto los{' '}
              <a 
                href={PDF} 
                target="_blank" 
                rel="noopener noreferrer"
                className={styles.link}
              >
                términos y condiciones
              </a>
            </label>
          </div>

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={handleClose}
            >
              Cancelar
            </button>
            <button
              type="button"
              className={styles.acceptButton}
              onClick={handleAcceptClick}
              disabled={!hasAccepted}
            >
              Aceptar y Publicar
            </button>
          </div>
        </div>
      </motion.div>
    </ModalOverlay>
  );
};

export default TermsAndConditionsModal;

